import { Editor } from '@tiptap/core';
import { NumberedNodes, NumberedNodeType } from '../../tiptap/numbering';
import { InternalLinkFormat } from '../../tiptap/extensions/plugins/InternalLink';
import { findTextOccurrences } from '../../tiptap/tiptapHelper';
import { extractNumbering } from './referenceCandidatePatterns';

export interface GuideInfo {
  id: string;
  docId: number;
  docName: string;
}

const CATEGORY_TO_PATTERN_KEY = { table: 'tables', figure: 'figures', heading: 'headings' } as const;
const NODE_TYPES: Record<'table' | 'figure' | 'heading', NumberedNodeType[]> = {
  table: ['tableFigure'],
  figure: ['imageFigure', 'chartFigure'],
  heading: ['heading'],
};

export function autoMapInternalReferences(
  editor: Editor,
  numberedNodes: NumberedNodes,
  candidates: string[],
  category: 'table' | 'figure' | 'heading',
): void {
  const nodeTypes = NODE_TYPES[category];

  replaceAll(editor, candidates, (text) => {
    const numbering = extractNumbering(text, CATEGORY_TO_PATTERN_KEY[category]);
    if (!numbering) return null;

    const matches = [...numberedNodes.values()].filter((n) => nodeTypes.includes(n.type) && n.numbering === numbering);
    if (matches.length !== 1) return null;

    const isIEEEStyle = category === 'heading' && text.startsWith('[');
    const format = isIEEEStyle ? InternalLinkFormat.CitationIEEEStyle : InternalLinkFormat.Default;
    return { type: 'internalLink', attrs: { id: matches[0].id, type: matches[0].type, format } };
  });
}

export function autoMapGuides(editor: Editor, candidates: string[], guides: GuideInfo[]): void {
  replaceAll(editor, candidates, (text) => {
    const matches = guides.filter((g) => g.docName.toLowerCase() === text.toLowerCase());
    if (matches.length !== 1) return null;

    const guide = matches[0];
    return { type: 'guideLink', attrs: { guideId: guide.id, docId: guide.docId, docName: guide.docName } };
  });
}

function replaceAll(
  editor: Editor,
  candidates: string[],
  resolve: (text: string) => { type: string; attrs: Record<string, unknown> } | null,
): void {
  const replacements: { from: number; to: number; type: string; attrs: Record<string, unknown> }[] = [];

  for (const text of candidates) {
    const resolved = resolve(text);
    if (!resolved) continue;
    for (const pos of findTextOccurrences(editor.state.doc, text)) {
      replacements.push({ ...pos, ...resolved });
    }
  }

  if (replacements.length === 0) return;

  // Deduplicate overlapping replacements, preferring longer matches
  replacements.sort((a, b) => a.from - b.from || (b.to - b.from) - (a.to - a.from));
  const deduped: typeof replacements = [];
  let lastEnd = -1;
  for (const r of replacements) {
    if (r.from >= lastEnd) {
      deduped.push(r);
      lastEnd = r.to;
    }
  }

  // Apply end-to-start to preserve positions
  const { tr, schema } = editor.state;
  for (let i = deduped.length - 1; i >= 0; i--) {
    const { from, to, type, attrs } = deduped[i];
    tr.replaceWith(from, to, schema.nodes[type].create(attrs));
  }
  editor.view.dispatch(tr);
}
